import { Placement } from '@popperjs/core';
import React, { ReactNode, FC, useMemo, useContext, useRef, useState, HTMLAttributes } from 'react';
import { ThemeContext } from 'styled-components';

import useResize from '../../../hooks/#useResize';
import Tooltip from '../../tips/Tooltip';
import { StyledMessage, StyledIcon, StyledIconCol, StyledMessageCol, StyledTip } from './FormErrorMessage.styles';
import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';

export interface FormErrorMessageProps extends HTMLAttributes<HTMLInputElement> {
  /** Custom error message. Alternative to using the `error`prop. */
  children?: ReactNode;
  /** Error message. If an object, it should contain a `message` prop. */
  error?: string | { message: ReactNode };
  /** I */
  errorType?: 'error' | 'warning' | 'caution';
}

const FormErrorMessageDefaultProps: Partial<FormErrorMessageProps> = {
  errorType: 'error',
};

const FormErrorMessage: FC<FormErrorMessageProps> = ({ children, error, errorType, ...props }) => {
  const [hasEllipsis, setHasEllipsis] = useState(false);
  const theme = useContext(ThemeContext);
  const errorMessage = useMemo(() => {
    if (typeof error === 'string') {
      return error;
    }

    if (typeof error === 'object' && error !== null) {
      return error.message;
    }

    return children;
  }, [children, error]);

  const tooltipOffset = useMemo(() => ({ placement }: { placement: Placement}): [number, number] => {
    if (placement === 'bottom') {
      return [0, 16];
    }
    return [0, 8];
  }, []);

  const messageRef = useRef<HTMLDivElement>();

  useResize(messageRef, () => {
    const messages: HTMLDivElement[] = Array.from(messageRef?.current.querySelectorAll('[data-test-message-type]'));
    const newHasEllipsis = (
      (messageRef.current.scrollWidth - messageRef.current.offsetWidth > 1) ||
        (messageRef.current.scrollHeight - messageRef.current.offsetHeight > 1) ||
        messages.some((message) => (
          (message.scrollWidth - message.offsetWidth > 1) || (message.scrollHeight - message.offsetHeight > 1)
        ))
    );

    setHasEllipsis(newHasEllipsis);
  });

  const icon = (
    <StyledIconCol>
      <StyledIcon
        as={(errorType === 'warning' || errorType === 'caution') ? WarningIcon : theme?.icons?.error}
        $errorType={errorType}
      />
    </StyledIconCol>
  );

  const content = (
    <StyledMessage
      data-testid="FormErrorMessage"
      data-live="polite"
      {...props}
      $errorType={errorType}
    >
      {icon}
      <StyledMessageCol ref={messageRef}>
        {errorMessage}
      </StyledMessageCol>
    </StyledMessage>
  );

  if (hasEllipsis) {
    return (
      <Tooltip
        tip={
          <StyledTip $errorType={errorType}>
            {icon}
            <StyledMessageCol>
              {errorMessage}
            </StyledMessageCol>
          </StyledTip>
        }
        offset={tooltipOffset}
        trigger="hover"
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

FormErrorMessage.defaultProps = FormErrorMessageDefaultProps;

export default FormErrorMessage;
